import { useGeld } from '../lib/geld';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { AlertTriangle, Calculator, Loader2, MapPin, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatDateLong, formatHours, formatMoney, formatTime, rateTypeLabel } from '../lib/utils';
import { shiftKind, KIND_LABEL, KIND_STYLE } from '../lib/shift-kind';
import type { ComplianceWarning, LeaveType, OverwerkSaldo, RateType, VerlengingLoon } from '../types';
import { objectColor } from './CalendarLegend';
import { AfwijkingPanel } from './AfwijkingPanel';
import { VerschuivingPanel } from './VerschuivingPanel';
import { fmtDuur, verlengingTekst, verlengingVan } from './Verlengd';
import { ICON, Notice, Sheet, Skeleton } from './ui';

interface Props {
  open: boolean;
  onClose: () => void;
  shiftId: number | null;
  initialDate: string | null;
  initialStartTime?: string | null;
}

const LEAVE_LABELS: Record<string, string> = {
  work: 'Werk',
  vakantie: 'Vakantie',
  ziekte: 'Ziekte',
  buitengewoon: 'Buitengewoon verlof',
  bijzonder_verlof: 'Bijzonder verlof',
};

const capFirst = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/** Verloop in de kleur van de dienst, gelijk aan het overzicht. */
const HERO_BG: Record<ReturnType<typeof shiftKind>, string> = {
  vroeg: 'bg-gradient-to-r from-sky-500/25 via-sky-500/10 to-transparent',
  laat: 'bg-gradient-to-r from-orange-500/25 via-orange-500/10 to-transparent',
  nacht: 'bg-gradient-to-r from-indigo-500/30 via-indigo-500/10 to-transparent',
  verlof: 'bg-gradient-to-r from-emerald-500/25 via-emerald-500/10 to-transparent',
  ziek: 'bg-gradient-to-r from-rose-500/25 via-rose-500/10 to-transparent',
  geruild: 'bg-gradient-to-r from-slate-500/20 to-transparent',
};

/** Paneel van een dienst: desktop van rechts, mobiel van onderen. */
export function ShiftDrawer({ open, onClose, shiftId, initialDate, initialStartTime }: Props) {
  const { geld, label } = useGeld();
  const qc = useQueryClient();
  const isEdit = shiftId != null;

  const [form, setForm] = useState({
    shift_date: '',
    start_time: '07:00',
    end_time: '15:00',
    object_id: null as number | null,
    status: 'scheduled',
    label: '',
    notes: '',
    leave_type: 'work',
  });
  const [warnings, setWarnings] = useState<ComplianceWarning[]>([]);

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['shift', shiftId],
    queryFn: () => api.shifts.get(shiftId!),
    enabled: isEdit,
  });

  const { data: objects = [] } = useQuery({
    queryKey: ['objects'],
    queryFn: api.objects.list,
  });

  useEffect(() => {
    if (isEdit && existing) {
      setForm({
        shift_date: existing.shift_date,
        start_time: existing.start_time,
        end_time: existing.end_time,
        object_id: existing.object_id,
        status: existing.status,
        label: existing.label ?? '',
        notes: existing.notes ?? '',
        leave_type: existing.leave_type ?? 'work',
      });
    } else if (initialDate) {
      setForm((f) => ({
        ...f,
        shift_date: initialDate,
        start_time: initialStartTime ?? f.start_time,
        end_time: initialStartTime != null ? addHours(initialStartTime, 8) : f.end_time,
        object_id: f.object_id ?? objects[0]?.id ?? null,
      }));
    }
  }, [isEdit, existing, initialDate, initialStartTime, objects]);

  // CAO-controle (vertraagd tijdens het typen)
  useEffect(() => {
    if (!form.shift_date || !form.start_time || !form.end_time) return;
    if (form.leave_type !== 'work') {
      setWarnings([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.cao.checkCompliance({
          shift_date: form.shift_date,
          start_time: form.start_time,
          end_time: form.end_time,
          exclude_shift_id: shiftId ?? undefined,
        });
        setWarnings(res.warnings);
      } catch {
        setWarnings([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.shift_date, form.start_time, form.end_time, form.leave_type, shiftId]);

  const selectedObject = useMemo(() => objects.find((o) => o.id === form.object_id) ?? null, [objects, form.object_id]);

  const hoursPreview = useMemo(() => {
    const [sh, sm] = form.start_time.split(':').map(Number);
    const [eh, em] = form.end_time.split(':').map(Number);
    let mins = eh * 60 + em - (sh * 60 + sm);
    if (mins <= 0) mins += 24 * 60;
    return mins / 60;
  }, [form.start_time, form.end_time]);

  const reiskostenPreview = useMemo(() => {
    if (!selectedObject?.distance_km) return 0;
    return selectedObject.distance_km * 2 * 0.23;
  }, [selectedObject]);

  // Loonopbouw per tarief: segmenten met hetzelfde tarief samengevoegd.
  const perTarief = useMemo(() => {
    const segs = existing?.segments ?? [];
    const map = new Map<string, { rate: RateType; multiplier: number; hours: number; amount: number }>();
    for (const s of segs) {
      const key = `${s.rate_type}|${s.multiplier}`;
      const row = map.get(key) ?? { rate: s.rate_type, multiplier: s.multiplier, hours: 0, amount: 0 };
      row.hours += s.hours;
      row.amount += s.total_amount;
      map.set(key, row);
    }
    return [...map.values()];
  }, [existing]);

  const payload = () => ({
    shift_date: form.shift_date,
    start_time: form.start_time,
    end_time: form.end_time,
    object_id: form.object_id,
    status: form.status,
    label: form.label || null,
    notes: form.notes || null,
    leave_type: form.leave_type,
  });

  const createMut = useMutation({
    mutationFn: () => api.shifts.create(payload() as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
  });

  const updateMut = useMutation({
    mutationFn: () => api.shifts.update(shiftId!, payload() as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] });
      qc.invalidateQueries({ queryKey: ['shift', shiftId] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.shifts.delete(shiftId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
  });

  if (!open) return null;

  const crossesMidnight = form.end_time <= form.start_time;
  const isLeave = form.leave_type !== 'work';
  const loading = isEdit && loadingExisting && !existing;

  // Kop: bij bewerken de opgeslagen dienst, bij een nieuwe dienst wat je nu invult.
  const head =
    isEdit && existing
      ? {
          date: existing.shift_date,
          start: existing.start_time,
          end: existing.end_time,
          kind: shiftKind(existing),
          object: existing.object_naam_snapshot ?? existing.object?.naam ?? null,
        }
      : {
          date: form.shift_date,
          start: form.start_time,
          end: form.end_time,
          kind: shiftKind({
            start_time: form.start_time,
            end_time: form.end_time,
            leave_type: form.leave_type as LeaveType,
            deviation_reason: null,
          }),
          object: selectedObject?.naam ?? null,
        };
  const kindStyle = KIND_STYLE[head.kind];
  const verlengd = isEdit && existing ? verlengingVan(existing) : null;
  const saveError = (createMut.error || updateMut.error || deleteMut.error) as Error | null;
  const saving = createMut.isPending || updateMut.isPending;

  const title = loading ? 'Dienst' : head.date ? capFirst(formatDateLong(head.date)) : isEdit ? 'Dienst' : 'Nieuwe dienst';
  const sub = loading || isEdit ? undefined : 'Nieuwe dienst';

  return (
    <Sheet
      open={open}
      onClose={onClose}
      breed
      title={title}
      sub={sub}
      footer={
        <div className="space-y-2">
          {saveError && <Notice tone="err">{saveError.message}</Notice>}
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => (isEdit ? updateMut.mutate() : createMut.mutate())}
              disabled={saving || loading}
              className="btn-primary"
            >
              {saving && <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden />}
              {isEdit ? 'Opslaan' : 'Toevoegen'}
            </button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="space-y-4" aria-busy="true" aria-label="Laden">
          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start lg:gap-6">
          {/* Links: de dienst zelf */}
          <div className="space-y-4">
            <div className={cn('rounded-xl border-l-[3px] p-4', kindStyle.bar, HERO_BG[head.kind])}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="t-kpi text-slate-50">
                  {formatTime(head.start)}–{formatTime(head.end)}
                </span>
                <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ring-white/10', kindStyle.text)}>
                  <span className={cn('h-1.5 w-1.5 rounded-full', kindStyle.dot)} aria-hidden />
                  {KIND_LABEL[head.kind]}
                </span>
                {verlengd && (
                  <span className="inline-flex items-center rounded-full bg-amber-400/10 px-2 py-0.5 text-xs font-medium text-amber-200 ring-1 ring-inset ring-amber-300/20">
                    Verlengd +{fmtDuur(verlengd.minuten)}
                  </span>
                )}
              </div>
              {verlengd && <p className="mt-1.5 text-sm text-slate-300">{verlengingTekst(verlengd)}</p>}
              {head.object && (
                <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-sm text-slate-300">
                  <MapPin className={cn(ICON.sm, 'shrink-0')} aria-hidden />
                  <span className="truncate">{head.object}</span>
                </div>
              )}
              <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2 border-t border-white/10 pt-3">
                <Kerngetal label="Uren" value={formatHours(isEdit && existing ? existing.total_hours : hoursPreview)} />
                <Kerngetal
                  label={label}
                  value={isEdit && existing ? geld(existing.total_gross, existing.shift_date) : 'Na opslaan'}
                  money={isEdit && !!existing}
                />
                <Kerngetal label="Reiskosten" value={formatMoney(isEdit && existing ? existing.reiskosten : isLeave ? 0 : reiskostenPreview)} />
              </dl>
            </div>

            <Paneel titel="Tijden">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div className="col-span-2 sm:col-span-1">
                  <label className="label" htmlFor="sd-date">Datum</label>
                  <input id="sd-date" type="date" className="input" value={form.shift_date} onChange={(e) => setForm({ ...form, shift_date: e.target.value })} />
                </div>
                <div>
                  <label className="label" htmlFor="sd-start">Begin</label>
                  <input id="sd-start" type="time" className="input" step="900" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                </div>
                <div>
                  <label className="label" htmlFor="sd-end">Einde</label>
                  <input id="sd-end" type="time" className="input" step="900" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                Duur <span className="t-num font-medium text-slate-200">{formatHours(hoursPreview)}</span>
                {crossesMidnight && ', loopt door tot de volgende dag'}
              </p>
              {!isEdit && (
                <div className="mt-3">
                  <label className="label" htmlFor="sd-type">Soort</label>
                  <select id="sd-type" className="input" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}>
                    {Object.entries(LEAVE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                  {isLeave && (
                    <p className="mt-1.5 text-xs text-slate-400">
                      {form.leave_type === 'ziekte' ? 'Ziekte wordt doorbetaald volgens de CAO.' : 'Verlof wordt betaald tegen uurloon plus gemiddelde toeslag.'}
                    </p>
                  )}
                </div>
              )}
              {warnings.length > 0 && (
                <div className="mt-3 space-y-2">
                  {warnings.map((w) => (
                    <Notice key={w.code} tone={w.level === 'red' ? 'err' : 'warn'}>
                      <span className="flex items-start gap-2">
                        <AlertTriangle className={cn(ICON.sm, 'mt-0.5 shrink-0')} aria-hidden />
                        <span>{w.message}</span>
                      </span>
                    </Notice>
                  ))}
                </div>
              )}
            </Paneel>

            <Paneel titel="Object">
              <div className="flex items-center gap-2">
                <select
                  id="sd-object"
                  aria-label="Object"
                  className="input flex-1"
                  value={form.object_id ?? ''}
                  onChange={(e) => setForm({ ...form, object_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">Geen object</option>
                  {objects
                    .filter((o) => o.active)
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.naam}
                      </option>
                    ))}
                </select>
                {selectedObject && <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: objectColor(selectedObject) }} aria-hidden />}
              </div>
              {selectedObject && (selectedObject.address_street || selectedObject.city || selectedObject.distance_km != null) && (
                <p className="mt-2 text-xs text-slate-400">
                  {[selectedObject.address_street, selectedObject.city].filter(Boolean).join(', ')}
                  {selectedObject.distance_km != null && (
                    <span className="t-num text-slate-500">
                      {selectedObject.address_street || selectedObject.city ? ', ' : ''}
                      {String(selectedObject.distance_km).replace('.', ',')} km enkele reis
                    </span>
                  )}
                </p>
              )}
            </Paneel>

            <Paneel titel="Notities">
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                <div>
                  <label className="label" htmlFor="sd-label">Label</label>
                  <input id="sd-label" type="text" className="input" placeholder="Optioneel" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                </div>
                <div>
                  <label className="label" htmlFor="sd-notes">Notitie</label>
                  <textarea id="sd-notes" className="input min-h-[42px]" rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
            </Paneel>
          </div>

          {/* Rechts: wat het oplevert en wat er anders ging */}
          <div className="space-y-4">
            <Paneel titel="Berekening" icon={Calculator}>
              {isEdit && existing ? (
                <>
                  {perTarief.length > 0 ? (
                    <dl className="space-y-1.5 text-sm">
                      {perTarief.map((r) => (
                        <div key={`${r.rate}-${r.multiplier}`} className="flex items-baseline justify-between gap-3">
                          <dt className="min-w-0 truncate text-slate-300">
                            {rateTypeLabel(r.rate)} <span className="t-num text-xs text-slate-500">{formatHours(r.hours)} × {formatMoney(r.hours > 0 ? r.amount / r.hours : 0)}</span>
                          </dt>
                          <dd className="t-num text-slate-100">{formatMoney(r.amount)}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="text-sm text-slate-400">Nog geen berekening voor deze dienst.</p>
                  )}
                  <dl className="mt-3 space-y-1.5 border-t border-surface-line pt-3 text-sm">
                    <Row label="Basis" value={formatMoney(existing.total_base)} />
                    <Row label="Toeslagen" value={formatMoney(existing.total_ort)} />
                    <Row label="Reiskosten" value={formatMoney(existing.reiskosten)} />
                    <div className="flex justify-between pt-1 font-semibold">
                      <dt className="text-slate-100">Bruto incl. reiskosten</dt>
                      <dd className="t-num text-money">{formatMoney(existing.total_gross + existing.reiskosten)}</dd>
                    </div>
                  </dl>
                  {existing.verlenging && <VerlengingBlok v={existing.verlenging} />}
                  {existing.overwerk && <OverwerkBlok o={existing.overwerk} />}
                </>
              ) : (
                <p className="text-sm text-slate-400">{isLeave ? 'Verlof en ziekte worden na opslaan berekend.' : 'De berekening per tarief zie je na het opslaan.'}</p>
              )}
            </Paneel>

            {isEdit && existing && (
              <Paneel>
                <AfwijkingPanel shift={existing} />
              </Paneel>
            )}

            {isEdit && existing && <VerschuivingPanel shiftId={existing.id} />}

            {isEdit && (
              <button
                type="button"
                onClick={() => {
                  if (confirm('Dienst verwijderen?')) deleteMut.mutate();
                }}
                disabled={deleteMut.isPending}
                className="inline-flex items-center gap-1.5 text-sm text-red-300/80 transition-colors duration-fast hover:text-red-300"
              >
                <Trash2 className={ICON.sm} aria-hidden />
                Dienst verwijderen
              </button>
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
}

function Kerngetal({ label, value, money }: { label: string; value: string; money?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="t-label">{label}</dt>
      <dd className={cn('t-num mt-0.5 text-base font-semibold', money ? 'text-money' : 'text-slate-50')}>{value}</dd>
    </div>
  );
}

/** Vak in de dienst-modal: rustige rand, kleine kop. */
function Paneel({ titel, icon: Icon, children }: { titel?: string; icon?: typeof Calculator; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-surface-line p-4">
      {titel && (
        <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-100">
          {Icon && <Icon className={cn(ICON.sm, 'text-slate-400')} aria-hidden />}
          {titel}
        </h3>
      )}
      {children}
    </section>
  );
}

/** Wat de verlenging zelf oplevert: loon, verschuivingstoeslag en overwerk boven de 152u-grens. */
function VerlengingBlok({ v }: { v: VerlengingLoon }) {
  const tijden = v.delen.map((d) => `${d.start}–${d.end}`).join(' + ');
  return (
    <div className="mt-3 border-t border-surface-line pt-3 text-sm">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h4 className="font-medium text-slate-200">Verlenging · {tijden}</h4>
        <span className="t-num text-xs text-slate-500">{formatHours(v.uren)}</span>
      </div>
      <dl className="space-y-1.5">
        <Row label={`Loon ${formatHours(v.uren)} × ${formatMoney(v.uurloon)}`} value={formatMoney(v.basis)} />
        {v.toeslagen > 0 && <Row label="Tariefopslag" value={formatMoney(v.toeslagen)} />}
        {v.verschuiving && v.verschuiving.bedrag > 0 && (
          <Row label={`Verschuivingstoeslag ${v.verschuiving.pct}%`} value={formatMoney(v.verschuiving.bedrag)} />
        )}
        {v.overwerk_uren > 0 && (
          <Row label={`Overwerk boven grens ${formatHours(v.overwerk_uren)} × 50%`} value={formatMoney(v.overwerk_toeslag)} />
        )}
        <div className="flex justify-between pt-1 font-semibold">
          <dt className="text-slate-100">Verlenging levert op</dt>
          <dd className="t-num text-money">{formatMoney(v.totaal)}</dd>
        </div>
      </dl>
      <p className="mt-2 text-xs text-slate-500">Het loon staat al in de dienst hierboven. De verschuivingstoeslag komt apart op je loonstrook; het overwerk boven de grens wordt uitbetaald over de laatste uren van de periode.</p>
    </div>
  );
}

/** Overwerksaldo: waar de periode staat t.o.v. de grens, vóór en na deze dienst. */
function OverwerkBlok({ o }: { o: OverwerkSaldo }) {
  const pct = (uren: number) => Math.min(100, Math.max(0, (uren / o.grens) * 100));
  return (
    <div className="mt-3 border-t border-surface-line pt-3 text-sm">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h4 className="font-medium text-slate-200">Overwerk · periode {o.periode.period_number}</h4>
        <span className="t-num text-xs text-slate-500">grens {formatHours(o.grens)}</span>
      </div>
      <div className="relative h-2 overflow-hidden rounded-full bg-surface-line" role="img"
        aria-label={`${formatHours(o.na)} van ${formatHours(o.grens)} bereikt na deze dienst`}>
        <div className="absolute inset-y-0 left-0 bg-slate-500" style={{ width: `${pct(o.voor)}%` }} />
        <div className="absolute inset-y-0 bg-money" style={{ left: `${pct(o.voor)}%`, width: `${pct(o.na) - pct(o.voor)}%` }} />
      </div>
      <dl className="mt-2 space-y-1.5">
        <Row label="Voor deze dienst" value={formatHours(o.voor)} />
        <Row label="Deze dienst" value={formatHours(o.deze_dienst)} />
        <Row label="Na deze dienst" value={formatHours(o.na)} />
        {o.dienst_overwerk_uren > 0 ? (
          <Row label="Overwerk in deze dienst" value={`${formatHours(o.dienst_overwerk_uren)} · +${formatMoney(o.dienst_overwerk_toeslag)}`} />
        ) : (
          <Row label={o.tot_grens > 0 ? 'Tot overwerk' : 'Overwerkgrens'} value={o.tot_grens > 0 ? formatHours(o.tot_grens) : 'bereikt'} />
        )}
        <Row label="Periode totaal" value={formatHours(o.periode_uren)} />
        <Row
          label="Overwerk hele periode"
          value={o.periode_overwerk_uren > 0 ? `${formatHours(o.periode_overwerk_uren)} · +${formatMoney(o.periode_overwerk_toeslag)}` : '0u'}
        />
      </dl>
      <p className="mt-2 text-xs text-slate-500">Verlof en ziekte van de hele periode tellen mee voor de grens.</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-400">{label}</dt>
      <dd className="t-num text-slate-200">{value}</dd>
    </div>
  );
}

function addHours(hhmm: string, delta: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  const total = (h * 60 + m + delta * 60) % (24 * 60);
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}
